'use client'

import React, { useCallback, useMemo, useState } from 'react'

import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  X,
} from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import Popover from '@/components/common/PopoverV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'

interface DateTimePickerV2Props {
  date: Date | undefined
  onSelect: (date: Date | undefined) => void
  placeholder?: string
  buttonClassName?: string
  popoverClassName?: string
  hideClearButton?: boolean
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const

const QUICK_TIME_OPTIONS = [
  { label: '9 AM', minutes: 9 * 60 },
  { label: 'Noon', minutes: 12 * 60 },
  { label: '2 PM', minutes: 14 * 60 },
  { label: '5 PM', minutes: 17 * 60 },
  { label: '8 PM', minutes: 20 * 60 },
] as const

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, monthIndex) => ({
  label: format(new Date(2000, monthIndex, 1), 'MMMM'),
  value: String(monthIndex),
}))

const YEAR_OPTIONS = Array.from({ length: 41 }, (_, index) =>
  String(2000 + index),
)

const MINUTES_IN_DAY = 24 * 60
const MAX_TIME_MINUTES = MINUTES_IN_DAY - 1
const TIME_NUDGE_MINUTES = 15

const createRoundedDate = () => {
  const date = new Date()
  date.setSeconds(0, 0)
  const roundedMinutes =
    Math.ceil(date.getMinutes() / TIME_NUDGE_MINUTES) * TIME_NUDGE_MINUTES

  if (roundedMinutes >= 60) {
    date.setHours(date.getHours() + 1, 0)
  } else {
    date.setMinutes(roundedMinutes)
  }

  return date
}

const getTimeMinutes = (date: Date) => {
  return date.getHours() * 60 + date.getMinutes()
}

const formatTime = (minutes: number) => {
  const date = new Date(2000, 0, 1)
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0)
  return format(date, 'h:mm')
}

const getPeriod = (minutes: number) => {
  return minutes >= 12 * 60 ? 'PM' : 'AM'
}

const setDateTimeMinutes = (date: Date, minutes: number) => {
  const nextDate = new Date(date)
  nextDate.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0)
  return nextDate
}

const clampTimeMinutes = (minutes: number) => {
  return Math.min(MAX_TIME_MINUTES, Math.max(0, minutes))
}

const parseTimeInput = (value: string, fallbackPeriod: 'AM' | 'PM') => {
  const normalizedValue = value.trim().toLowerCase().replace(/\s+/g, '')
  const match = /^(\d{1,2})(?::([0-5]\d))?(a|am|p|pm)?$/.exec(normalizedValue)

  if (!match) {
    return null
  }

  const rawHour = Number(match[1])
  const minute = match[2] ? Number(match[2]) : 0
  const meridiem = match[3]

  if (meridiem) {
    if (rawHour < 1 || rawHour > 12) {
      return null
    }

    const period = meridiem.startsWith('p') ? 'PM' : 'AM'
    const hour =
      period === 'PM'
        ? rawHour === 12
          ? 12
          : rawHour + 12
        : rawHour === 12
          ? 0
          : rawHour

    return hour * 60 + minute
  }

  if (rawHour > 23) {
    return null
  }

  if (rawHour === 0 || rawHour > 12) {
    return rawHour * 60 + minute
  }

  const hour =
    fallbackPeriod === 'PM'
      ? rawHour === 12
        ? 12
        : rawHour + 12
      : rawHour === 12
        ? 0
        : rawHour

  return hour * 60 + minute
}

const buildCalendarDays = (visibleMonth: Date) => {
  const startDate = startOfWeek(startOfMonth(visibleMonth), {
    weekStartsOn: 0,
  })

  return Array.from({ length: 42 }, (_, index) => addDays(startDate, index))
}

const DateTimePickerV2: React.FC<DateTimePickerV2Props> = ({
  date,
  onSelect,
  placeholder = 'Select date and time...',
  buttonClassName,
  popoverClassName,
  hideClearButton = false,
}) => {
  const [open, setOpen] = useState(false)
  const [draftDate, setDraftDate] = useState<Date>(() =>
    date ? new Date(date) : createRoundedDate(),
  )
  const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
    startOfMonth(date ?? new Date()),
  )
  const [timeInputValue, setTimeInputValue] = useState<string>(() =>
    formatTime(getTimeMinutes(date ? new Date(date) : createRoundedDate())),
  )

  const calendarDays = useMemo(
    () => buildCalendarDays(visibleMonth),
    [visibleMonth],
  )
  const timeMinutes = getTimeMinutes(draftDate)

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const nextDate = date ? new Date(date) : createRoundedDate()
        setDraftDate(nextDate)
        setVisibleMonth(startOfMonth(nextDate))
        setTimeInputValue(formatTime(getTimeMinutes(nextDate)))
      }

      setOpen(nextOpen)
    },
    [date],
  )

  const updateDraftTime = useCallback((minutes: number) => {
    const nextMinutes = clampTimeMinutes(minutes)
    setDraftDate((currentDate) => setDateTimeMinutes(currentDate, nextMinutes))
    setTimeInputValue(formatTime(nextMinutes))
  }, [])

  const handleDaySelect = useCallback((day: Date) => {
    setVisibleMonth(startOfMonth(day))
    setDraftDate((currentDate) => {
      const nextDate = new Date(currentDate)
      nextDate.setFullYear(day.getFullYear(), day.getMonth(), day.getDate())
      return nextDate
    })
  }, [])

  const handleMonthSelect = useCallback((monthIndex: string) => {
    setVisibleMonth((currentMonth) =>
      startOfMonth(new Date(currentMonth.getFullYear(), Number(monthIndex), 1)),
    )
  }, [])

  const handleYearSelect = useCallback((year: string) => {
    setVisibleMonth((currentMonth) =>
      startOfMonth(new Date(Number(year), currentMonth.getMonth(), 1)),
    )
  }, [])

  const handlePreviousMonth = useCallback(() => {
    setVisibleMonth((currentMonth) => subMonths(currentMonth, 1))
  }, [])

  const handleNextMonth = useCallback(() => {
    setVisibleMonth((currentMonth) => addMonths(currentMonth, 1))
  }, [])

  const handleNudgeEarlier = useCallback(() => {
    updateDraftTime(timeMinutes - TIME_NUDGE_MINUTES)
  }, [timeMinutes, updateDraftTime])

  const handleNudgeLater = useCallback(() => {
    updateDraftTime(timeMinutes + TIME_NUDGE_MINUTES)
  }, [timeMinutes, updateDraftTime])

  const handlePeriodToggle = useCallback(() => {
    const hour = Math.floor(timeMinutes / 60)
    const minutes = timeMinutes % 60
    const nextHour = hour >= 12 ? hour - 12 : hour + 12
    updateDraftTime(nextHour * 60 + minutes)
  }, [timeMinutes, updateDraftTime])

  const normalizeTimeInput = useCallback(() => {
    const parsedMinutes = parseTimeInput(timeInputValue, getPeriod(timeMinutes))

    if (parsedMinutes === null) {
      setTimeInputValue(formatTime(timeMinutes))
      return
    }

    updateDraftTime(parsedMinutes)
  }, [timeInputValue, timeMinutes, updateDraftTime])

  const handleTimeInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value
      setTimeInputValue(value)

      const parsedMinutes = parseTimeInput(value, getPeriod(timeMinutes))
      if (parsedMinutes !== null) {
        setDraftDate((currentDate) =>
          setDateTimeMinutes(currentDate, clampTimeMinutes(parsedMinutes)),
        )
      }
    },
    [timeMinutes],
  )

  const handleTimeInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        normalizeTimeInput()
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        setTimeInputValue(formatTime(timeMinutes))
      }
    },
    [normalizeTimeInput, timeMinutes],
  )

  const handleQuickTimeClick = useCallback(
    (minutes: number) => {
      updateDraftTime(minutes)
    },
    [updateDraftTime],
  )

  const handleCancel = useCallback(() => {
    setOpen(false)
  }, [])

  const handleApply = useCallback(() => {
    const parsedMinutes = parseTimeInput(timeInputValue, getPeriod(timeMinutes))
    const selectedDate =
      parsedMinutes === null
        ? draftDate
        : setDateTimeMinutes(draftDate, clampTimeMinutes(parsedMinutes))

    onSelect(selectedDate)
    setOpen(false)
  }, [draftDate, onSelect, timeInputValue, timeMinutes])

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onSelect(undefined)
      setOpen(false)
    },
    [onSelect],
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="relative w-full">
        <Popover.Trigger asChild>
          <button
            type="button"
            className={twMerge(
              TEXT_INPUT_CLASSNAME,
              'flex items-center justify-between gap-2 text-left',
              date && !hideClearButton ? 'pr-8' : undefined,
              buttonClassName,
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <CalendarDays
                size={16}
                className="shrink-0 text-primary-500 dark:text-primary-400"
              />
              <span
                className={twMerge(
                  'truncate',
                  date
                    ? 'text-primary-900 dark:text-primary-100'
                    : 'text-primary-500 dark:text-primary-400',
                )}
              >
                {date ? format(date, 'MMM d, yyyy h:mm a') : placeholder}
              </span>
            </span>
          </button>
        </Popover.Trigger>
        {date && !hideClearButton ? (
          <button
            type="button"
            aria-label="Clear date and time"
            className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 rounded-sm p-0.5 text-primary-500 transition-colors hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-100"
            onClick={handleClear}
          >
            <X size={15} />
          </button>
        ) : null}
      </div>
      <Popover.Content
        align="start"
        sideOffset={0}
        collisionPadding={4}
        className={twMerge(
          'flex max-h-[min(620px,calc(var(--radix-popover-content-available-height)-8px))] w-[360px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-md p-0 shadow-xl',
          popoverClassName,
        )}
        portal={true}
      >
        <div className="shrink-0 px-4 pt-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex shrink-0 items-center gap-1.5">
              <Select
                value={String(visibleMonth.getMonth())}
                onValueChange={handleMonthSelect}
              >
                <SelectTrigger className="h-7 w-[100px] rounded-[5px] border-transparent bg-transparent px-1.5 text-base font-semibold hover:border-primary-200 dark:hover:border-primary-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_OPTIONS.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={String(visibleMonth.getFullYear())}
                onValueChange={handleYearSelect}
              >
                <SelectTrigger className="h-7 w-[88px] rounded-[5px] border-transparent bg-transparent px-1.5 text-base font-semibold hover:border-primary-200 dark:hover:border-primary-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent viewportClassName="max-h-64">
                  {YEAR_OPTIONS.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant="ghost-compact"
                aria-label="Previous month"
                onClick={handlePreviousMonth}
                className="size-7 px-0 text-primary-500 dark:text-primary-400"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                type="button"
                variant="ghost-compact"
                aria-label="Next month"
                onClick={handleNextMonth}
                className="size-7 px-0 text-primary-500 dark:text-primary-400"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto px-4">
          <div className="grid grid-cols-7 gap-y-1.5">
            {WEEKDAY_LABELS.map((weekday) => (
              <div
                key={weekday}
                className="flex h-6 items-center justify-center text-xs font-medium text-primary-400 dark:text-primary-500"
              >
                {weekday}
              </div>
            ))}
            {calendarDays.map((day) => {
              const isSelected = isSameDay(day, draftDate)
              const isCurrentMonth = isSameMonth(day, visibleMonth)
              const isTodayDate = isToday(day)

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleDaySelect(day)}
                  className={twMerge(
                    'mx-auto flex size-8 items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-primary-900',
                    isCurrentMonth
                      ? 'text-primary-900 hover:bg-primary-100 dark:text-primary-100 dark:hover:bg-primary-800'
                      : 'text-primary-300 hover:bg-primary-50 dark:text-primary-700 dark:hover:bg-primary-800/60',
                    isTodayDate &&
                      !isSelected &&
                      'border border-accent-300 text-accent-500 dark:text-accent-300',
                    isSelected &&
                      'bg-accent-400 font-semibold text-white shadow-sm shadow-accent-400/25 hover:bg-accent-500 dark:bg-accent-400 dark:hover:bg-accent-500',
                  )}
                >
                  {format(day, 'd')}
                </button>
              )
            })}
          </div>

          <div className="mt-4 border-t border-primary-200 py-3 dark:border-primary-800">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-primary-400 dark:text-primary-500">
                Time
              </div>
              <div className="inline-flex h-8 items-center gap-1 rounded-md border border-primary-200 bg-primary-50 p-0.5 shadow-sm dark:border-primary-800 dark:bg-primary-950">
                <button
                  type="button"
                  aria-label="Move time earlier"
                  onClick={handleNudgeEarlier}
                  className="inline-flex h-7 w-8 items-center justify-center rounded-[5px] text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-50"
                >
                  <Minus size={16} />
                </button>
                <input
                  aria-label="Time"
                  value={timeInputValue}
                  onChange={handleTimeInputChange}
                  onBlur={normalizeTimeInput}
                  onKeyDown={handleTimeInputKeyDown}
                  className="h-7 w-[76px] rounded-[5px] border border-primary-200 bg-white px-2 text-center text-base font-semibold tabular-nums text-primary-950 outline-none transition-[border-color,box-shadow,background-color] placeholder:text-primary-400 focus:border-accent-400 focus:ring-2 focus:ring-accent-400 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-50 dark:focus:border-accent-400/50 dark:focus:ring-accent-400/50"
                />
                <button
                  type="button"
                  aria-label="Move time later"
                  onClick={handleNudgeLater}
                  className="inline-flex h-7 w-8 items-center justify-center rounded-[5px] text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-50"
                >
                  <Plus size={16} />
                </button>
                <button
                  type="button"
                  aria-label="Toggle AM or PM"
                  onClick={handlePeriodToggle}
                  className="inline-flex h-7 min-w-11 items-center justify-center rounded-[5px] border border-primary-200 bg-white px-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-100 dark:hover:bg-primary-800"
                >
                  {getPeriod(timeMinutes)}
                </button>
              </div>
            </div>

            <div className="mb-2 grid grid-cols-5 gap-2">
              {QUICK_TIME_OPTIONS.map((option) => {
                const isActive = option.minutes === timeMinutes

                return (
                  <Button
                    key={option.label}
                    type="button"
                    variant="soft-compact"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleQuickTimeClick(option.minutes)}
                    className={twMerge(
                      'w-full px-1 text-xs',
                      isActive &&
                        'border-accent-300 bg-accent-50 text-accent-500 hover:bg-accent-50 dark:border-accent-500/50 dark:bg-accent-400/15 dark:text-accent-300',
                    )}
                  >
                    {option.label}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 gap-2 border-t border-primary-200 bg-white p-1.5 dark:border-primary-800 dark:bg-primary-900">
          <Button
            type="button"
            variant="outline-compact"
            onClick={handleCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary-compact"
            onClick={handleApply}
            className="flex-1"
          >
            <Check size={17} />
            Apply
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}

export default DateTimePickerV2
